import type { Vehicle } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { formatVehicleLabel } from '../../utils';

interface SelectItemVehicleProps {
  vehicle: Vehicle;
  isSelected?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

const SelectItemVehicle = ({ vehicle, isSelected, disabled, onClick }: SelectItemVehicleProps) => {
  const { t } = useTranslation('multiselects');
  const label = formatVehicleLabel(vehicle);
  const hasDeviceAssociated = !!vehicle.deviceId;
  const associationTooltip = hasDeviceAssociated ? t('vehicle.associated') : t('vehicle.not_associated');

  return (
    <button
      type='button'
      disabled={disabled}
      onClick={onClick}
      aria-pressed={isSelected}
      title={label}
      className={cn(
        'flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left outline-none',
        'hover:bg-gray-3 focus-visible:bg-gray-3',
        'disabled:cursor-not-allowed disabled:opacity-50',
        isSelected && 'bg-gray-3'
      )}
    >
      <div className='flex min-w-0 items-center gap-2'>
        <Tooltip content={associationTooltip} position='bottom' align='start' portal={false}>
          <Icon name={hasDeviceAssociated ? 'link' : 'unlink'} className={hasDeviceAssociated ? 'text-primary' : 'text-gray-dark'} />
        </Tooltip>
        <Typography variant='text-md' color='secondary' className='line-clamp-1'>
          {label}
        </Typography>
      </div>
      {isSelected && <Icon name='check' className='shrink-0 text-primary' />}
    </button>
  );
};

export default SelectItemVehicle;
